// Peças das telas de plano: selo do plano, ícones, selo "Em breve" e o carregamento do /api/eu.
import { useCallback, useEffect, useId, useState } from 'react';
import {
  CICLOS, type Ciclo, MESES_GRATIS_NO_ANUAL, NOME_DO_CICLO, NOME_DO_PLANO, PRECO_CENTAVOS, type Plano, type PlanoPago,
  economiaNoAnual, emReais, parcelaDoAnual, precoEmReais,
} from '@truco6/planos';
import { cores } from '@truco6/ui';
import type { OrigemDoToque } from '@truco6/planos';
import { api, type Eu, lerEu } from '../comum/api.ts';
import type { Sessao } from '../comum/conta.ts';
import { compra, medir } from '../comum/medicao.ts';

// Brilho, corpo e borda de cada selo (tokens do canvas: F04, B04, B05, B06).
const METAL: Record<Plano, [string, string, string]> = {
  gratis: [cores.ink300, cores.ground700, cores.line600],
  bronze: [cores.cream050, cores.bronze400, cores.bronze700],
  prata: [cores.ink050, cores.prata300, cores.prata600],
  ouro: [cores.cream050, cores.gold400, cores.gold600],
};

export function Moeda({ plano, tamanho = 40 }: { plano: Plano; tamanho?: number }) {
  const id = useId();
  const [brilho, corpo, borda] = METAL[plano];
  const traco = plano === 'gratis' ? cores.ink300 : cores.ink900;
  return (
    <svg className="moeda" width={tamanho} height={tamanho} viewBox="0 0 32 32" role="img" aria-label={'Selo do plano ' + NOME_DO_PLANO[plano]}>
      <defs>
        <radialGradient id={id} cx="35%" cy="30%" r="75%">
          <stop offset="0" stopColor={brilho} />
          <stop offset=".45" stopColor={corpo} />
          <stop offset="1" stopColor={borda} />
        </radialGradient>
      </defs>
      <circle cx="16" cy="16" r="15" fill={`url(#${CSS.escape(id)})`} />
      <circle cx="16" cy="16" r="11" fill="none" stroke={traco} strokeOpacity=".3" strokeWidth="1.5" />
      <path d="M10 20l-1.5-8 4.5 3 3-5 3 5 4.5-3-1.5 8z" fill="none" stroke={traco} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const CAMINHOS = {
  check: 'M5 12.5l4.5 4.5L19 7.5',
  checkCirculo: 'M7.5 12.5l3 3 6-6.5',
  renovar: 'M20 11a8 8 0 0 0-14.3-4.9L4 8M4 4v4h4M4 13a8 8 0 0 0 14.3 4.9L20 16m0 4v-4h-4',
  fechar: 'M9 9l6 6M15 9l-6 6',
  volta: 'M9 14L4 9l5-5M4 9h10.5a5.5 5.5 0 0 1 0 11H11',
  cartao: 'M3 6h18v12H3zM3 10h18M7 15h3',
  pix: 'M12 3l9 9-9 9-9-9zM8 12h8',
  cadeado: 'M6 11h12v9H6zM8.5 11V8a3.5 3.5 0 0 1 7 0v3',
  calendario: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  seta: 'M9 6l6 6-6 6',
  relogio: 'M12 7v5l3 2',
} as const;

export function Icone({ nome, tamanho = 22 }: { nome: keyof typeof CAMINHOS; tamanho?: number }) {
  const circulo = nome === 'checkCirculo' || nome === 'fechar' || nome === 'relogio';
  return (
    <svg className="icone" width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {circulo && <circle cx="12" cy="12" r="10" />}
      <path d={CAMINHOS[nome]} />
    </svg>
  );
}

export function EmBreve() {
  return <span className="em-breve">Em breve</span>;
}

// Selo dourado da vantagem do anual.
export function SeloEconomia() {
  return <span className="selo-economia">Economize {MESES_GRATIS_NO_ANUAL} meses</span>;
}

// Abas Mensal e Anual. Cada aba mostra as condições e os preços do seu ciclo.
export function AbasCiclo({ ciclo, aoMudar }: { ciclo: Ciclo; aoMudar: (c: Ciclo) => void }) {
  return (
    <div className="abas-ciclo" role="tablist" aria-label="Ciclo de cobrança">
      {CICLOS.map((c) => (
        <button key={c} role="tab" aria-selected={ciclo === c} className={ciclo === c ? 'on' : undefined} onClick={() => aoMudar(c)}>
          {NOME_DO_CICLO[c]}
          {c === 'anual' && <span className="menos">−{MESES_GRATIS_NO_ANUAL} meses</span>}
        </button>
      ))}
    </div>
  );
}

// Preço do plano no ciclo. No anual, a parcela por mês em destaque, o total à vista e o mensal riscado.
export function PrecoDoCiclo({ plano, ciclo }: { plano: PlanoPago; ciclo: Ciclo }) {
  if (ciclo === 'mensal') return <p className="preco">{precoEmReais(plano)}<small>/mês</small></p>;
  return (
    <div className="preco-anual">
      <p className="preco">{emReais(parcelaDoAnual(plano))}<small>/mês</small></p>
      <p className="a-vista">{precoEmReais(plano, 'anual')} à vista por ano</p>
    </div>
  );
}

// Linha de vantagem do anual: 12 mensalidades riscadas e a economia.
export function VantagemDoAnual({ plano }: { plano: PlanoPago }) {
  return (
    <p className="linha-vantagem">
      <SeloEconomia />
      <span><s className="riscado">{emReais(12 * PRECO_CENTAVOS[plano])}</s> por ano no mensal. Você economiza <b className="sem-quebra">{emReais(economiaNoAnual(plano))}</b>.</span>
    </p>
  );
}

// Lê o /api/eu e deixa recarregar (depois de assinar, cancelar ou trocar).
export function useEu(sessao: Sessao) {
  const [estado, setEstado] = useState<{ eu: Eu | null; erro: string | null }>({ eu: null, erro: null });
  const carregar = useCallback(async (): Promise<Eu | null> => {
    try {
      const eu = await lerEu(sessao);
      setEstado({ eu, erro: null });
      return eu;
    } catch (e) {
      setEstado((a) => ({ eu: a.eu, erro: (e as Error).message }));
      return null;
    }
  }, [sessao]);
  useEffect(() => { void carregar(); }, [carregar]);
  return { ...estado, carregar };
}

// Assinar direto, sem tela de confirmar (quem ainda não assina): cria o Checkout da Stripe e vai para ele. O GA4 recebe
// o begin_checkout com a origem do toque. novaAba: a mesa abre o Checkout em outra aba e continua conectada nesta.
export async function irParaCheckout(sessao: Sessao, plano: PlanoPago, ciclo: Ciclo, origem: OrigemDoToque, novaAba = false): Promise<void> {
  medir('begin_checkout', compra(plano, ciclo, { payment_type: 'cartao', origem }));
  // A aba nova abre no toque (o navegador bloqueia a que abre depois da resposta) e recebe o endereço quando ele chega.
  const aba = novaAba ? window.open('', '_blank') : null;
  try {
    const r = await api<{ url: string }>(sessao, 'POST', '/assinatura', { plano, metodo: 'cartao', ciclo });
    if (aba) aba.location.href = r.url; else location.assign(r.url);
  } catch (e) {
    aba?.close();
    throw e;
  }
}
